import moment from "moment";

import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { SiteUIModel } from "@/Common/Models/Sites/SiteUIModel";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { PortfolioStore } from "@/Portfolio/Stores/PortfolioStore";

class MyNotesSettingsContainerHandler extends BaseOperationHandler {
    private helper: MyleHelper;

    constructor() {
        super();

        this.helper = new MyleHelper();

        this.RegisterOperation(LocalOperations.Portfolio.MyNotes_SearchSettings_ShowOptionsButtonClick, this.DisplayMyNotesSettings);
        this.RegisterOperation(LocalOperations.Portfolio.MyNotes_SearchSettings_ClinicianFilterTextChanged, this.ClinicianFilterChanged);
        this.RegisterOperation(LocalOperations.Portfolio.MyNotes_OnSettingsContainerCheckboxChange, this.OnSettingsContainerCheckboxChange);
        this.RegisterOperation(LocalOperations.Portfolio.MyNotes_SearchSettings_CancelClick, this.OnCancelClick);
        this.RegisterOperation(LocalOperations.Portfolio.MyNotes_SearchSettings_OkClick, this.OnOkClick);
        this.RegisterOperation(LocalOperations.Portfolio.MyNotes_SearchSettings_ColumnOrderingPopup_Open, this.ShowColumnOrderingPopup);
        this.RegisterOperation(LocalOperations.Portfolio.MyNotes_SearchSettings_ColumnOrderingPopup_Close, this.CloseColumnOrderingPopup);
        this.RegisterOperation(LocalOperations.Portfolio.MyNotes_SearchSettings_FromDateChanged, this.FromDateChanged);
        this.RegisterOperation(LocalOperations.Portfolio.MyNotes_SearchSettings_ToDateChanged, this.ToDateChanged);
        this.RegisterOperation(LocalOperations.Misc.MyleApp_Container_Click, this.CloseSearchFilterPopup, false);
    }

    private CloseSearchFilterPopup(request: LocalEvent, store: PortfolioStore): void {
        if (store.NotesInboxModel.get("ShowMyNotesDisplaySettings") === true) {
            this.OnOkClick(request, store);
            store.EmitChangeToListeners();
        }
    }

    private FromDateChanged(request: LocalEvent, store: PortfolioStore): void {
        let dateFrom = request.Get<Date>("value");
        dateFrom = dateFrom && moment(dateFrom).startOf("day").toDate();
        store.NotesInboxModel = store.NotesInboxModel.set("DateFrom", dateFrom);
    }

    private ToDateChanged(request: LocalEvent, store: PortfolioStore): void {
        let dateTo = request.Get<Date>("value");
        dateTo = dateTo && moment(dateTo).endOf("day").toDate();
        store.NotesInboxModel = store.NotesInboxModel.set("DateTo", dateTo);
    }

    private OnOkClick(request: LocalEvent, store: PortfolioStore): void {
        store.NotesInboxModel = store.NotesInboxModel.set("ShowMyNotesDisplaySettings", false);

        store.PortfolioActions.LoadMyNotes(store.NotesInboxModel);
    }

    private ShowColumnOrderingPopup(request: LocalEvent, store: PortfolioStore): void {
        store.NotesInboxModel = store.NotesInboxModel.set("ColumnOrderingPopupOpen", true);
        store.NotesInboxModel = store.NotesInboxModel.set("ShowMyNotesDisplaySettings", false);
        sessionStore.showGlobalOverlay(true);
    }

    private CloseColumnOrderingPopup(request: LocalEvent, store: PortfolioStore): void {
        store.NotesInboxModel = store.NotesInboxModel.set("ColumnOrderingPopupOpen", false);
        store.NotesInboxModel = store.NotesInboxModel.set("ShowMyNotesDisplaySettings", false);
        sessionStore.hideGlobalOverlay();
    }

    private DisplayMyNotesSettings(request: LocalEvent, store: PortfolioStore): void {
        var open = store.NotesInboxModel.get("ShowMyNotesDisplaySettings");
        store.NotesInboxModel = store.NotesInboxModel.set("ShowMyNotesDisplaySettings", !open);
    }

    private ClinicianFilterChanged(request: LocalEvent, store: PortfolioStore): void {
        store.NotesInboxModel = store.NotesInboxModel.set("CliniciansSearchText", request.Get<string>("value"));
        store.NotesInboxModel = store.NotesInboxModel.set("CliniciansFiltered", store.GetMyNotesCliniciansBasedOnSitesAndQuery());
    }

    private OnSettingsContainerCheckboxChange(request: LocalEvent, store: PortfolioStore): void {
        var target = request.Get<CheckboxTarget>("target");
        var checked = request.Get<boolean>("checked");
        var id = request.Get<string>("id");

        switch (target) {
            case CheckboxTarget.Myle_Portfolio_MyNotes_Sites:
                store.NotesInboxModel = store.NotesInboxModel.set("Sites", store.helper.FindUpdateICheckableItem<SiteUIModel>(store.NotesInboxModel.get("Sites"), id, checked));
                store.NotesInboxModel = store.NotesInboxModel.set("CliniciansFiltered", store.GetMyNotesCliniciansBasedOnSitesAndQuery());
                break;

            case CheckboxTarget.Myle_Portfolio_MyNotes_Clinicians:
                store.NotesInboxModel = store.NotesInboxModel.set("CliniciansGlobalCache", this.helper.FindUpdateICheckableItem<EmployeeUIModel>(store.NotesInboxModel.get("CliniciansGlobalCache"), id, checked));
                store.NotesInboxModel = store.NotesInboxModel.set("CliniciansFiltered", store.GetMyNotesCliniciansBasedOnSitesAndQuery());
                break;

            case CheckboxTarget.Myle_Portfolio_MyNotes_SitesControlCheck:
                store.NotesInboxModel = store.NotesInboxModel.set("SelectAllSitesChecked", checked);
                var sites = store.helper.ChangeCheckStateFromAllICheckable(store.NotesInboxModel.get("Sites"), checked);
                store.NotesInboxModel = store.NotesInboxModel.set("Sites", sites);
                store.NotesInboxModel = store.NotesInboxModel.set("CliniciansFiltered", store.GetMyNotesCliniciansBasedOnSitesAndQuery());
                break;

            case CheckboxTarget.Myle_Portfolio_MyNotes_CliniciansControlCheck:
                store.NotesInboxModel = store.NotesInboxModel.set("SelectAllCliniciansChecked", checked);
                var list = store.GetMyNotesCliniciansBasedOnSitesAndQuery();
                var listOfClinicianIds = list.toArray().map(i => i.Id);
                var c = store.helper.FindUpdateICheckableItems(store.NotesInboxModel.get("CliniciansGlobalCache"), listOfClinicianIds, checked);
                store.NotesInboxModel = store.NotesInboxModel.set("CliniciansGlobalCache", c);
                var checkedList = store.GetMyNotesCliniciansBasedOnSitesAndQuery();
                store.NotesInboxModel = store.NotesInboxModel.set("CliniciansFiltered", checkedList);
                break;
            case CheckboxTarget.Myle_Portfolio_MyNotes_Status:
                store.NotesInboxModel = store.NotesInboxModel.set("Status", store.helper.FindUpdateICheckableItem<CheckboxUIModel>(store.NotesInboxModel.get("Status"), id, checked));
                break;
            case CheckboxTarget.Myle_Portfolio_MyNotes_StatusControlCheck:
                store.NotesInboxModel = store.NotesInboxModel.set("SelectAllStatusChecked", checked);
                store.NotesInboxModel = store.NotesInboxModel.set("Status", this.helper.ChangeCheckStateFromAllICheckable(store.NotesInboxModel.get("Status"), checked));
                break;

            case CheckboxTarget.Myle_Portfolio_MyNotes_OnlySelectedSites:
                store.NotesInboxModel = store.NotesInboxModel.set("ShowOnlySelectedSites", checked);
                break;

            default:
                break;
        }
    }

    private OnCancelClick(request: LocalEvent, store: PortfolioStore): void {
        store.NotesInboxModel = store.NotesInboxModel.set("ShowMyNotesDisplaySettings", false);
    }
}

export { MyNotesSettingsContainerHandler };
